// src/web/client/src/components/analytics/SmartTiming.jsx
// Smart Message Timing Component - Pro Feature

import React, { useState, useEffect } from 'react';
import { safeApi, isFeatureError } from '@/utils/api';

export default function SmartTiming({ compact = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shopTiming, setShopTiming] = useState(null);
  const [smartTimingEnabled, setSmartTimingEnabled] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsError, setSettingsError] = useState(null);

  useEffect(() => {
    fetchShopTiming();
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await safeApi.get('/timing/settings');
      if (data && !isFeatureError(data)) {
        setSmartTimingEnabled(data.enabled === true);
      }
    } catch (err) {
      console.error('Failed to load Smart Timing setting:', err);
    }
  };

  const toggleSmartTiming = async () => {
    const previous = smartTimingEnabled;
    const newValue = !previous;
    setSavingSettings(true);
    setSettingsError(null);
    setSmartTimingEnabled(newValue); // optimistic — reverted below on failure
    try {
      const res = await safeApi.put('/timing/settings', { enabled: newValue });
      if (!res || res.success !== true || isFeatureError(res)) {
        throw new Error(res?.error || 'Save failed');
      }
    } catch (err) {
      console.error('Failed to save Smart Timing setting:', err);
      // Honest failure: put the toggle back and say so — never pretend the
      // setting saved when the server didn't confirm it.
      setSmartTimingEnabled(previous);
      setSettingsError('Could not save — Smart Timing was left unchanged. Please try again.');
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchShopTiming = async () => {
    setLoading(true);
    try {
      const data = await safeApi.get('/timing/shop');
      
      if (isFeatureError(data)) {
        setError(data);
        return;
      }
      
      setShopTiming(data.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch shop timing:', err);
      setError({ message: 'Failed to load timing data' });
    } finally {
      setLoading(false);
    }
  };


  // Pro Gate
  if (error && error.code === 'FEATURE_NOT_AVAILABLE') {
    return <ProGateMessage />;
  }

  if (loading) {
    return <LoadingState />;
  }

  if (compact) {
    return <CompactView data={shopTiming} />;
  }

  const hasTimingData = (shopTiming?.total_analyzed || 0) > 0;

  return (
    <div className="space-y-6">
      {/* Header with Toggle */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Smart timing</h2>
            <p className="text-gray-500 mt-1">Send each review request when that customer usually opens emails — worked out from their real opens.</p>
          </div>
        </div>

        {/* Smart Timing Toggle */}
        <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                smartTimingEnabled ? 'bg-green-100' : 'bg-gray-100'
              }`}>
                <span className="text-xl">{smartTimingEnabled ? '✅' : '⏸️'}</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  Smart Timing is {smartTimingEnabled ? 'On' : 'Off'}
                </h3>
                <p className="text-sm text-gray-600">
                  {smartTimingEnabled
                    ? 'Review emails are sent when each customer is most likely to open — based on their real open history. Never earlier than your review delay.'
                    : 'Review emails follow your standard review delay.'
                  }
                </p>
              </div>
            </div>
            <button
              onClick={toggleSmartTiming}
              disabled={savingSettings}
              aria-label="Toggle Smart Timing"
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                smartTimingEnabled ? 'bg-green-500' : 'bg-gray-300'
              } ${savingSettings ? 'opacity-50' : ''}`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform ${
                  smartTimingEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {settingsError && (
            <p className="mt-3 text-sm text-red-600" role="alert">
              ⚠️ {settingsError}
            </p>
          )}
        </div>


        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <StatCard
            label="Messages Analyzed"
            value={shopTiming?.total_analyzed || 0}
            icon="📊"
          />
          {/* With zero analyzed opens the backend returns labelled industry
              defaults (10 AM / Tuesday). Rendering those as if computed would
              be an invented stat — show an honest empty state instead and let
              the values appear only once real opens exist. */}
          <StatCard
            label="Best Hour"
            value={hasTimingData ? shopTiming.best_hours?.[0]?.label || '—' : '—'}
            sublabel={hasTimingData ? null : 'Collecting data from your first opens'}
            icon="🕐"
          />
          <StatCard
            label="Best Day"
            value={hasTimingData ? shopTiming.best_days?.[0]?.label || '—' : '—'}
            sublabel={hasTimingData ? null : 'Collecting data from your first opens'}
            icon="📅"
          />
          <StatCard
            label="Peak Open Rate"
            value={hasTimingData ? `${shopTiming.best_hours?.[0]?.percentage || 0}%` : '—'}
            sublabel={hasTimingData ? null : 'Collecting data from your first opens'}
            icon="📈"
          />
        </div>
      </div>

      {/* Hourly Distribution Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Hourly Open Distribution</h3>
        <HourlyChart data={shopTiming?.hourly_distribution || []} />
      </div>

      {/* Daily Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📅 Daily Performance</h3>
        <DailyChart data={shopTiming?.daily_distribution || []} />
      </div>

      {/* Best Times */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Best Hours */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Top Performing Hours</h3>
          {!hasTimingData && (
            <p className="text-sm text-gray-500">
              No opens recorded yet — your top hours will appear here as customers start opening emails.
            </p>
          )}
          <div className="space-y-3">
            {(hasTimingData ? shopTiming?.best_hours || [] : []).slice(0, 5).map((hour, i) => (
              <div key={hour.hour} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium ${
                    i === 0 ? 'bg-yellow-100 text-yellow-700' :
                    i === 1 ? 'bg-gray-100 text-gray-700' :
                    i === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="font-medium">{hour.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${hour.percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-12 text-right">{hour.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Best Days */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📆 Best Days to Send</h3>
          {!hasTimingData && (
            <p className="text-sm text-gray-500">
              No opens recorded yet — your best days will appear here as customers start opening emails.
            </p>
          )}
          <div className="space-y-3">
            {(hasTimingData ? shopTiming?.best_days || [] : []).map((day, i) => (
              <div key={day.day} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-sm font-medium ${
                    i === 0 ? 'bg-green-100 text-green-700' :
                    i === 1 ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-50 text-gray-500'
                  }`}>
                    {i + 1}
                  </span>
                  <span className="font-medium">{day.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full"
                      style={{ width: `${day.percentage}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-500 w-12 text-right">{day.percentage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {shopTiming?.recommendations && shopTiming.recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">💡 AI Recommendations</h3>
          <div className="space-y-3">
            {shopTiming.recommendations.map((rec, i) => (
              <div 
                key={i}
                className={`p-4 rounded-lg flex items-start gap-3 ${
                  rec.impact === 'high' ? 'bg-green-50 border border-green-200' :
                  rec.impact === 'medium' ? 'bg-blue-50 border border-blue-200' :
                  'bg-gray-50 border border-gray-200'
                }`}
              >
                <span className="text-xl">
                  {rec.impact === 'high' ? '🎯' : rec.impact === 'medium' ? '💡' : 'ℹ️'}
                </span>
                <div>
                  <p className="font-medium text-gray-900">{rec.message}</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Impact: <span className="capitalize">{rec.impact}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

function CompactView({ data }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">⏰ Smart Timing</h3>
        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">PRO</span>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Best Time</span>
          <span className="font-medium">{data?.best_hours?.[0]?.label || '10:00 AM'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Best Day</span>
          <span className="font-medium">{data?.best_days?.[0]?.label || 'Tuesday'}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-gray-500">Data Points</span>
          <span className="font-medium">{data?.total_analyzed || 0}</span>
        </div>
      </div>
      
      {data?.recommendations?.[0] && (
        <div className="mt-4 p-3 bg-purple-50 rounded-lg">
          <p className="text-sm text-purple-700">
            💡 {data.recommendations[0].message}
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, sublabel }) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <span className="text-2xl">{icon}</span>
      <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
    </div>
  );
}

function HourlyChart({ data }) {
  const maxOpens = Math.max(...data.map(d => d.opens), 1);
  
  return (
    <div className="overflow-x-auto">
      <div className="flex items-end justify-between gap-1 h-40 min-w-[600px]">
        {data.map((hour) => (
          <div key={hour.hour} className="flex-1 flex flex-col items-center">
            <div 
              className={`w-full rounded-t transition-all ${
                hour.percentage > 10 ? 'bg-purple-500' :
                hour.percentage > 5 ? 'bg-purple-400' :
                hour.percentage > 0 ? 'bg-purple-300' :
                'bg-gray-200'
              }`}
              style={{ height: `${(hour.opens / maxOpens) * 100}%`, minHeight: '4px' }}
              title={`${hour.label}: ${hour.opens} opens (${hour.percentage}%)`}
            />
            <span className="text-xs text-gray-400 mt-2 transform -rotate-45 origin-center">
              {hour.hour}
            </span>
          </div>
        ))}
      </div>
      <p className="text-center text-xs text-gray-400 mt-4">Hour of Day (0-23)</p>
    </div>
  );
}

function DailyChart({ data }) {
  const maxOpens = Math.max(...data.map(d => d.opens), 1);
  
  return (
    <div className="grid grid-cols-7 gap-2">
      {data.map((day) => (
        <div key={day.day} className="text-center">
          <div className="h-24 flex items-end justify-center mb-2">
            <div 
              className={`w-full max-w-[40px] rounded-t transition-all ${
                day.percentage > 15 ? 'bg-green-500' :
                day.percentage > 10 ? 'bg-green-400' :
                day.percentage > 5 ? 'bg-green-300' :
                'bg-gray-200'
              }`}
              style={{ height: `${(day.opens / maxOpens) * 100}%`, minHeight: '4px' }}
            />
          </div>
          <p className="text-xs font-medium text-gray-700">{day.label.slice(0, 3)}</p>
          <p className="text-xs text-gray-400">{day.percentage}%</p>
        </div>
      ))}
    </div>
  );
}


function LoadingState() {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="animate-pulse space-y-4">
        <div className="h-6 bg-gray-200 rounded w-1/3"></div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        <div className="grid grid-cols-4 gap-4 mt-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProGateMessage() {
  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <span className="text-3xl">⏰</span>
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">Smart Message Timing</h3>
      <p className="text-gray-600 mb-6">
        AI-powered optimal timing to maximize email opens and engagement.
      </p>
      <p className="text-sm text-purple-600">
        Upgrade to Pro plan to unlock this feature
      </p>
    </div>
  );
}